import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { usePermission } from 'hooks';
import { useSnackbar } from 'v3/components/Snackbar';
import Text from 'v3/components/Text';
import api, { cooperplaceApi } from 'services/api';
import Details from '../Details';
import Register from '../Register';
import { UserProvider } from './context';

function getEmptyUser() {
  return {
    profile: null,
    additionalProfiles: [],
    customPermissions: [],
    username: '',
    cpf: '',
    phonenumber: '',
    email: '',
    verifyEmail: '',
    password: '',
    verifyPassword: '',
    verified: false,
    change_password: true,
    company: null,
    companies: [],
    clients: [],
    shipping_companies: [],
    beneficiary: [],
    establishments: [],
    groups: [],
    tags: [],
    sites: [],
    costCenters: [],
    costCentersManaged: [],
    branchOffices: [],
  };
}

function getEmptyAudit() {
  return {
    data: [],
    page: 1,
    perPage: 5,
    totalItems: 0,
    totalPages: 1,
    loading: false,
  };
}

export default function UserShared() {
  const snackbar = useSnackbar();
  const params = useParams();
  const dispatch = useDispatch();

  const [user, setUser] = useState(getEmptyUser());
  const [prevUser, setPrevUser] = useState(user);
  const [isFetching, setFetching] = useState(false);
  const [errors, setErrors] = useState({});
  const [audit, setAudit] = useState(getEmptyAudit());
  const hasPermissionToEdit = usePermission('ALTERAR_USUARIO');
  const hasPermissionToCreate = usePermission('CADASTRAR_USUARIO');
  usePermission('VISUALIZAR_USUARIOS', { redirect: true });

  function normalizeAdditionalProfiles(data) {
    if (Array.isArray(data?.additionalProfiles)) {
      return data.additionalProfiles.map(item => {
        const profile = item.profile || {};
        return {
          id: profile.id || item.profile_id || item.id || item,
          name: profile.name || item.name,
        };
      });
    }

    if (Array.isArray(data?.additional_profiles)) {
      return data.additional_profiles.map(item =>
        typeof item === 'number'
          ? { id: item }
          : {
            id: item.profile_id || item.id,
            name: item.name,
          }
      );
    }

    return [];
  }

  function normalizeAdditionalPermissions(data) {
    const source =
      data?.additionalPermissions || data?.additional_permissions || [];

    if (!Array.isArray(source)) return [];

    return source.map(item => {
      const permission = item.permission || {};
      const actionValue =
        typeof item.action === 'boolean'
          ? item.action
            ? 'allow'
            : 'deny'
          : item.action;

      return {
        id: permission.id || item.permission_id || item.id,
        name: permission.name || item.name,
        group: permission.group || item.group,
        slug: permission.slug || item.slug,
        action: actionValue || 'allow',
        includedBy: item.createdBy?.username || item.included_by,
        includedAt: item.created_at || item.included_at,
      };
    });
  }

  function normalizeTags(data) {
    if (!Array.isArray(data?.tags)) return [];

    return data.tags
      .map((tag) => {
        const tagData = tag?.name && typeof tag.name === 'object' ? tag.name : tag;

        return {
          id: tag?.tag_id || tagData?.id || tag?.id,
          name: tagData?.name || tag?.name,
        };
      })
      .filter((tag) => tag.id);
  }

  function showSnackbar(text, options) {
    snackbar.show(<Text> {text} </Text>, options);
  }

  async function fetchUser() {
    try {
      setFetching(true);
      const response = await api.get(`users/${params.id}`);
      const normalizedUser = {
        ...response.data,
        additionalProfiles: normalizeAdditionalProfiles(response.data),
        customPermissions: normalizeAdditionalPermissions(response.data),
        tags: normalizeTags(response.data),
      };
      setUser(normalizedUser);
      setPrevUser(normalizedUser);
    } catch (ex) {
      showSnackbar('Não foi possível carregar esse usuário', {
        type: 'error',
      });
    } finally {
      setFetching(false);
    }
  }

  async function fetchUserAudit({ page, perPage, userId } = {}) {
    const targetUserId = userId || params.id;
    if (!targetUserId) return;

    try {
      setAudit(prev => ({ ...prev, loading: true }));
      const resolvedPage = page ?? audit.page;
      const resolvedPerPage = perPage ?? audit.perPage;
      const response = await cooperplaceApi.get(`users/${targetUserId}/audit`, {
        params: {
          page: resolvedPage,
          perPage: resolvedPerPage,
        },
      });

      const payload = response.data || {};
      const rows = payload.data || [];
      const meta = payload.meta || {};
      setAudit(prev => ({
        ...prev,
        data: rows,
        totalItems: meta.total || rows.length,
        totalPages: meta.last_page || meta.lastPage || 1,
        page: meta.current_page || resolvedPage,
        perPage: meta.per_page || resolvedPerPage,
        loading: false,
      }));
    } catch (error) {
      setAudit(prev => ({
        ...prev,
        data: [],
        totalItems: 0,
        totalPages: 1,
        loading: false,
      }));
    }
  }

  function setAuditPage(page) {
    setAudit(prev => ({ ...prev, page }));
  }

  function setAuditPerPage(perPage) {
    setAudit(prev => ({ ...prev, perPage, page: 1 }));
  }

  useEffect(() => {
    async function cloneUser() {
      try {
        setFetching(true);
        const response = await api.get(`users/${params.clone_id}`);
        const normalizedUser = {
          clients: response.data.clients,
          companies: response.data.companies,
          establishments: response.data.establishments,
          groups: response.data.groups,
          profile: response.data.profile,
          additionalProfiles: normalizeAdditionalProfiles(response.data),
          shipping_companies: response.data.shipping_companies,
          sites: response.data.sites,
          costCenters: response.data.costCenters,
          costCentersManaged: response.data.costCentersManaged,
          branchOffices: response.data.branchOffices,
          beneficiary: response.data.beneficiary,
          customPermissions: normalizeAdditionalPermissions(response.data),
        };
        setUser(normalizedUser);
        setPrevUser(normalizedUser);
      } catch (ex) {
        showSnackbar('Não foi possível carregar esse usuário', {
          type: 'error',
        });
      } finally {
        setFetching(false);
      }
    }

    if (params.clone_id) {
      cloneUser();
      return;
    }

    if (params.id) {
      fetchUser();
      return;
    }

    const emptyUser = getEmptyUser();
    setUser(emptyUser);
    setPrevUser(emptyUser);
    setErrors({});
    setAudit(getEmptyAudit());
    setFetching(false);
  }, [params.id, params.clone_id]);

  function setData(data) {
    setUser((prev) => ({ ...prev, ...data }));
  }

  function updatePrevUser() {
    setPrevUser(user);
  }

  function handleCancel() {
    setUser(prevUser);
  }

  useEffect(() => {
    if (params.id) {
      dispatch({
        type: 'VIEW_INSTANCE',
        instance: user?.username,
      });
    }
  }, [user]);

  return (
    <UserProvider
      value={{
        data: user,
        setData,
        isFetching,
        errors,
        setErrors,
        showSnackbar,
        handleCancel,
        updatePrevUser,
        prevUser,
        fetchUser,
        hasPermissionToEdit,
        hasPermissionToCreate,
        audit,
        fetchUserAudit,
        setAuditPage,
        setAuditPerPage,
      }}
    >
      {params.id ? <Details /> : <Register />}
    </UserProvider>
  );
}
